import { useCallback, useState } from 'react';
import type { AccessLogItem, Api } from '../api';
import { EVENT_LABEL, LOG_PAGE_SIZE, NOTABLE_EVENTS, ROLE, ROLE_LABEL } from '../admin/labels';
import { fmtDateTime, NONE } from '../admin/format';
import { usePagedList } from '../admin/usePagedList';
import { Pager } from './Pager';

/**
 * 감사 로그(2026-09-15 관리 화면, 관리자 전용). 서버 `GET /api/access-logs`.
 *
 * 무엇이 담기나: 접속 요청·승인·거절·종료, 기기 차단·이름 변경, 사용자 추가·변경·비활성화·비밀번호 초기화·삭제,
 * 로그인 토큰 재사용 탐지. 사용자 관리 이벤트의 "사용자" 칸은 **조작한 관리자**이고, 대상은 내용에 적혀 있다
 * (대상이 삭제돼도 기록이 남게 하려는 서버 규칙).
 */
export function AccessLogView({ api }: { api: Api }) {
  const [eventType, setEventType] = useState('');
  const fetchPage = useCallback(
    (offset: number) => api.listAccessLogs({ eventType: eventType || undefined, limit: LOG_PAGE_SIZE, offset }),
    [api, eventType],
  );
  const list = usePagedList<AccessLogItem>(fetchPage);

  return (
    <div className="view">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>감사 로그</h2>
        <div className="row">
          <select value={eventType} onChange={(e) => setEventType(e.target.value)} aria-label="이벤트 필터">
            <option value="">모든 이벤트</option>
            {Object.entries(EVENT_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <button className="ghost" onClick={list.reload} disabled={list.loading}>
            {list.loading ? '불러오는 중…' : '새로고침'}
          </button>
        </div>
      </div>
      <p className="hint" style={{ margin: '0 0 14px' }}>
        누가 언제 무엇을 했는지의 기록입니다. 사용자 관리 항목의 '사용자' 는 조작한 관리자이고, 대상은 내용에 있습니다.
      </p>

      {list.error && <div className="hint" style={{ color: 'var(--err)', marginBottom: 12 }}>{list.error}</div>}
      {list.items.length === 0 && !list.loading ? (
        <p className="hint">기록이 없습니다.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>시각</th>
                <th>이벤트</th>
                <th>사용자</th>
                <th>기기</th>
                <th>내용</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((l) => (
                <tr key={l.logId}>
                  <td className="nowrap">{fmtDateTime(l.createdAt)}</td>
                  <td className="nowrap">
                    <span className={`badge ${NOTABLE_EVENTS.has(l.eventType) ? 'warn' : ''}`}>
                      {EVENT_LABEL[l.eventType] ?? l.eventType}
                    </span>
                  </td>
                  <td>
                    {l.userName ? (
                      <>
                        <div>
                          {l.userName}
                          {/* 수퍼유저의 행위(§14.19) — 우리 조직 사람이 아니므로 표시해 둔다 */}
                          {l.userIsSu && <span className="badge" style={{ marginLeft: 6 }}>{ROLE_LABEL[ROLE.SU]}</span>}
                        </div>
                        <div className="hint">{l.userEmail}</div>
                      </>
                    ) : (
                      <span className="hint">{NONE}</span>
                    )}
                  </td>
                  <td>
                    {l.deviceCode ? (
                      <>
                        <div>{l.deviceName || '(이름 없음)'}</div>
                        <div className="hint">{l.deviceCode}</div>
                      </>
                    ) : (
                      <span className="hint">{NONE}</span>
                    )}
                  </td>
                  <td className="log-message">{l.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager total={list.total} limit={list.limit} offset={list.offset} onChange={list.goTo} disabled={list.loading} />
    </div>
  );
}
